@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginechat

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.DashboardTheme
import org.example.project.manager.LumaticaApiManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.AlignSelf
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.alignSelf
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexGrow
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.opacity
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.style
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style as DomStyle
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

private val layoutContactsJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun layoutContactsApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadLayoutContactUsers(firmenId: String): List<All> {
    if (firmenId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/users/${layoutContactsApiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { layoutContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadLayoutGlobalCompanies(currentFirmenId: String): List<All> {
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/global/${layoutContactsApiPart(currentFirmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { layoutContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadChatUserByGlobalId(globalId: String): All? {
    val companyId = globalId.substringBefore("_", "").trim()
    val userId = chatIdentityUserId(globalId)
    if (companyId.isBlank() || userId.isBlank()) return null
    val raw = runCatching {
        LumaticaApiManager.call("userverwaltung/company-user/${layoutContactsApiPart(companyId)}/${layoutContactsApiPart(userId)}")
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    val user = runCatching { layoutContactsJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull() ?: return null
    return user.copy(id = globalChatUserId(user.firmenId.ifBlank { companyId }, user.id))
}

suspend fun loadMissingChatUsers(rooms: List<All>, allUsers: Map<String, All>): Map<String, All> {
    val missingIds = rooms
        .flatMap(::roomParticipantIds)
        .distinct()
        .filter { it.isNotBlank() && it !in allUsers }
    if (missingIds.isEmpty()) return emptyMap()
    return missingIds.mapNotNull { id ->
        loadChatUserByGlobalId(id)?.let { user -> user.id to user }
    }.toMap()
}

suspend fun loadChatContactData(currentUserId: String, contactCompanyFilterId: String = ""): ChatContactsData {
    val currentUser = LoginService.getUser()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    val currentChatUserId = globalChatUserId(currentFirmenId, currentUser?.id.orEmpty())
    val effectiveFilterCompanyId = contactCompanyFilterId.trim()
    val mobileOnlyDriver = currentUser?.zugangDesktop == false && currentUser.zugangMobil
    val internalCompanies = if (currentFirmenId.isBlank()) {
        emptyList()
    } else {
        val internalUsers = loadLayoutContactUsers(currentFirmenId)
            .asSequence()
            .filter { it.id.isNotBlank() }
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .filter { it.firmenId == currentFirmenId }
            .filter { canSeeKontaktProfil(currentFirmenId, it) }
            .map { it.copy(id = globalChatUserId(it.firmenId, it.id)) }
            .sortedBy { userDisplayName(it).lowercase() }
            .toList()
        val companyLabel = internalUsers.firstOrNull()?.firma
            .orEmpty()
            .ifBlank { internalUsers.firstOrNull()?.kunde.orEmpty() }
            .ifBlank { currentUser?.kunde.orEmpty() }
        listOf(
            All(
                firmenId = currentFirmenId,
                firma = companyLabel,
                kunde = companyLabel,
                columnSettings = internalUsers
            )
        )
    }

    val externalCompanies = if (mobileOnlyDriver) {
        emptyList()
    } else {
        loadLayoutGlobalCompanies(currentFirmenId).mapNotNull { company ->
            val companyId = company.firmenId.trim()
            if (companyId.isBlank() || companyId == currentFirmenId) return@mapNotNull null
            val users = company.columnSettings
                .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
                .sortedBy { userDisplayName(it).lowercase() }
            if (users.isEmpty()) null else company.copy(columnSettings = users)
        }
    }

    val companies = (internalCompanies + externalCompanies).filter {
        it.columnSettings.isNotEmpty() &&
            (effectiveFilterCompanyId.isBlank() || it.firmenId.trim() == effectiveFilterCompanyId)
    }
    val users = companies.flatMap { company ->
        company.columnSettings.map { user ->
            if (user.id.contains("_")) user else user.copy(id = globalChatUserId(user.firmenId.ifBlank { company.firmenId }, user.id))
        }
    }
    val companyLabel = companies.firstOrNull { it.firmenId.trim() == currentFirmenId }?.firma
        .orEmpty()
        .ifBlank { companies.firstOrNull()?.firma.orEmpty() }
    return ChatContactsData(
        companyLabel = companyLabel,
        internalContacts = users,
        contactCompanies = companies,
        allUsers = users.associateBy { it.id } + listOfNotNull(currentUser).associateBy {
            globalChatUserId(it.firmenId, it.id)
        }
    )
}

private const val CHAT_POPUP_WIDTH = 760.0
private const val CHAT_POPUP_HEIGHT = 520.0
private const val CHAT_POPUP_MIN_WIDTH = 320.0
private const val CHAT_POPUP_MIN_HEIGHT = 240.0
private const val CHAT_MESSAGES_CONTAINER_ID = "chat-messages-container"

@Composable
private fun DesktopContactRow(
    user: All,
    canWrite: Boolean,
    showNewChat: Boolean,
    checked: Boolean,
    onChecked: (Boolean) -> Unit,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(10.px, 12.px, 10.px, 14.px)
            cursor(if (canWrite) "pointer" else "default")
            display(DisplayStyle.Grid)
            property(
                "grid-template-columns",
                if (showNewChat) "22px 64px minmax(0, 1fr)" else "64px minmax(0, 1fr)"
            )
            alignItems(AlignItems.Center)
            gap(10.px)
        }
        if (canWrite) onClick { onClick() }
    }) {
        if (showNewChat) {
            LumaticaCheckbox(
                checked = checked,
                onChange = onChecked,
                enabled = canWrite,
                fontSizePx = 14
            )
        }

        UserAvatarCircle(user = user, fallbackLabel = userDisplayName(user), sizePx = 60, background = UISettings.SALES_KPI_BG)

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                minWidth(0.px)
                gap(3.px)
            }
        }) {
            Span({
                style {
                    fontSize(16.px)
                    fontWeight("750")
                    color(DashboardTheme.TEXT_LIGHT)
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(userDisplayName(user)) }
            Span({
                style {
                    fontSize(13.px)
                    color(DashboardTheme.TEXT_MUTED)
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(userCompanyLabel(user)) }
        }
    }
}

private fun chatQueryParams(): Map<String, String> {
    val query = window.location.hash.substringAfter('?', "")
    if (query.isBlank()) return emptyMap()
    return query
        .split("&")
        .mapNotNull { entry ->
            val key = entry.substringBefore("=").trim()
            if (key.isBlank()) return@mapNotNull null
            key to (js("decodeURIComponent")(entry.substringAfter("=", "")) as String)
        }
        .toMap()
}

object ChatPopupStyles : StyleSheet() {
    val hiddenScroll by style {
        overflowY("auto")
        property("scrollbar-width", "none")
        property("-ms-overflow-style", "none")
        self + "::-webkit-scrollbar" style { display(DisplayStyle.None) }
    }
}

@Composable
fun ChatEngineLayout(
    popupMode: Boolean = false,
    onClose: (() -> Unit)? = null,
    embeddedInEngine: Boolean = false,
    conversationOnly: Boolean = false,
    presetUser: All? = null,
    contactCompanyFilterId: String = ""
) {
    org.jetbrains.compose.web.css.Style(ChatPopupStyles)

    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val currentUserIds = currentUserChatIds()
    val art = when (window.location.port) {
        "2000" -> NodeScope.NET
        else -> if (GlobalManager.currentFirmenId.isBlank()) NodeScope.SYS else NodeScope.CUS
    }
    val currentUserId = LoginService.getId().trim()
    val currentSenderId = currentSenderChatId(art)
    val canWrite = art == NodeScope.NET || RollenService.hasWritePermission("ChatModuleScreen")
    val presetTargetChatId = presetUser?.let { globalChatUserId(it.firmenId, chatIdentityUserId(it.id)) }.orEmpty()
    val roomVersion = GlobalManager.jsonTopicVersion("room.json")
    var smartLayout by remember(popupMode, embeddedInEngine, conversationOnly, presetTargetChatId) {
        mutableStateOf(conversationOnly)
    }

    var rooms by remember { mutableStateOf<List<All>>(emptyList()) }
    var contactCompanies by remember { mutableStateOf<List<All>>(emptyList()) }
    var allUsers by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var expandedCompanies by remember { mutableStateOf<Set<String>>(emptySet()) }
    var selectedUsers by remember { mutableStateOf(setOf<String>()) }
    var showNewChat by remember { mutableStateOf(false) }
    var selectedRoomId by remember { mutableStateOf<String?>(null) }
    var chatSearch by remember { mutableStateOf("") }
    var hoveredRoomId by remember { mutableStateOf<String?>(null) }
    var menuRoomId by remember { mutableStateOf<String?>(null) }
    var infoRoomId by remember { mutableStateOf<String?>(null) }
    var longPressTimer by remember { mutableStateOf<Int?>(null) }
    var suppressRoomClick by remember { mutableStateOf(false) }
    var showContactsView by remember { mutableStateOf(false) }
    var inputText by remember { mutableStateOf("") }
    var isSaving by remember { mutableStateOf(false) }
    var posX by remember { mutableStateOf(170.0) }
    var posY by remember { mutableStateOf(90.0) }
    var draggingWindow by remember { mutableStateOf(false) }
    var resizingWindow by remember { mutableStateOf(false) }
    var resizingSidebar by remember { mutableStateOf(false) }
    var startX by remember { mutableStateOf(0.0) }
    var startY by remember { mutableStateOf(0.0) }
    var popupWidth by remember { mutableStateOf(CHAT_POPUP_WIDTH) }
    var popupHeight by remember { mutableStateOf(CHAT_POPUP_HEIGHT) }
    var startWidth by remember { mutableStateOf(CHAT_POPUP_WIDTH) }
    var startHeight by remember { mutableStateOf(CHAT_POPUP_HEIGHT) }
    var sidebarWidth by remember { mutableStateOf(260.0) }

    DisposableEffect(popupMode) {
        val move: (Event) -> Unit = { ev ->
            val e = ev as MouseEvent
            if (draggingWindow) {
                val deltaX = e.clientX.toDouble() - startX
                val deltaY = e.clientY.toDouble() - startY
                val maxX = maxOf(0.0, window.innerWidth.toDouble() - popupWidth)
                val maxY = maxOf(0.0, window.innerHeight.toDouble() - popupHeight)
                posX = (posX + deltaX).coerceIn(0.0, maxX)
                posY = (posY + deltaY).coerceIn(0.0, maxY)
                startX = e.clientX.toDouble()
                startY = e.clientY.toDouble()
            } else if (resizingWindow) {
                val nextWidth = (startWidth + (e.clientX.toDouble() - startX))
                    .coerceIn(CHAT_POPUP_MIN_WIDTH, window.innerWidth.toDouble() - posX)
                val nextHeight = (startHeight + (e.clientY.toDouble() - startY))
                    .coerceIn(CHAT_POPUP_MIN_HEIGHT, window.innerHeight.toDouble() - posY)
                popupWidth = nextWidth
                popupHeight = nextHeight
            } else if (resizingSidebar) {
                val deltaX = e.clientX.toDouble() - startX
                sidebarWidth = (sidebarWidth + deltaX).coerceIn(170.0, 520.0)
                startX = e.clientX.toDouble()
            }
        }

        val up: (Event) -> Unit = {
            draggingWindow = false
            resizingWindow = false
            resizingSidebar = false
        }

        document.addEventListener("mousemove", move)
        document.addEventListener("mouseup", up)

        onDispose {
            document.removeEventListener("mousemove", move)
            document.removeEventListener("mouseup", up)
        }
    }

    fun upsertRoomState(updated: All) {
        rooms = mergeRoomLists(rooms.filterNot { it.id == updated.id }, listOf(updated))
        selectedRoomId = updated.id
    }

    fun openRoomMenu(room: All) {
        menuRoomId = room.id
        suppressRoomClick = true
    }

    fun clearRoomMenu() {
        menuRoomId = null
    }

    fun deleteRoomForCurrentUser(room: All) {
        val senderId = currentSenderChatId(art)
        if (room.id.isBlank() || senderId.isBlank()) return
        scope.launch {
            ChatApi.deleteRoomForParticipant(room.id, senderId)
            val freshRooms = loadStandaloneRooms(art, currentUserIds)
            rooms = freshRooms
            if (selectedRoomId == room.id) {
                selectedRoomId = freshRooms.firstOrNull()?.id
            }
            if (infoRoomId == room.id) infoRoomId = null
            clearRoomMenu()
        }
    }

    LaunchedEffect(art, currentUserId, contactCompanyFilterId) {
        val loadedRooms = loadStandaloneRooms(art, currentUserIds)
        rooms = loadedRooms
        val contactData = loadChatContactData(
            currentUserId = currentUserId,
            contactCompanyFilterId = contactCompanyFilterId
        )
        contactCompanies = contactData.contactCompanies
        expandedCompanies = contactData.contactCompanies.map { it.firmenId.ifBlank { it.id } }.toSet()
        allUsers = contactData.allUsers + loadMissingChatUsers(loadedRooms, contactData.allUsers)

        if (selectedRoomId == null || rooms.none { it.id == selectedRoomId }) {
            selectedRoomId = rooms.firstOrNull()?.id
        }
    }

    val filteredRooms = rooms.filter { room ->
        if (chatSearch.isBlank()) return@filter true
        roomTitle(room, currentUserIds, allUsers).contains(chatSearch, ignoreCase = true)
    }
    val filteredContactCompanies = contactCompanies.mapNotNull { company ->
        val label = company.firma.ifBlank { company.kunde.ifBlank { company.firmenId.ifBlank { company.id } } }
        if (chatSearch.isBlank()) {
            company
        } else {
            val users = company.columnSettings.filter { user ->
                label.contains(chatSearch, ignoreCase = true) ||
                    userDisplayName(user).contains(chatSearch, ignoreCase = true) ||
                    userCompanyLabel(user).contains(chatSearch, ignoreCase = true)
            }
            company.copy(columnSettings = users).takeIf { users.isNotEmpty() }
        }
    }

    val selectedRoom = rooms.firstOrNull { it.id == selectedRoomId }
    var roomBlocks by remember { mutableStateOf<List<All>>(emptyList()) }
    val headerTitle = if (smartLayout) {
        presetUser?.let(::shortUserDisplayName).orEmpty().ifBlank {
            selectedRoom?.let { roomTitle(it, currentUserIds, allUsers) }.orEmpty()
        }.ifBlank { "Chat" }
    } else {
        "Chat"
    }


    LaunchedEffect(selectedRoom?.id) {
        roomBlocks = selectedRoom?.id?.let { loadStandaloneMessages(art, it) }.orEmpty()
    }

    LaunchedEffect(art, currentUserId, selectedRoom?.id, roomVersion) {
        val participantId = currentUserIds.firstOrNull().orEmpty()
        if (participantId.isBlank()) return@LaunchedEffect
        val freshRooms = loadStandaloneRooms(art, currentUserIds)
        rooms = mergeRoomLists(rooms, freshRooms)
        val missingUsers = loadMissingChatUsers(freshRooms, allUsers)
        if (missingUsers.isNotEmpty()) allUsers = allUsers + missingUsers
        val activeRoomId = selectedRoomId
        if (activeRoomId == null || freshRooms.none { it.id == activeRoomId }) {
            selectedRoomId = freshRooms.firstOrNull()?.id
        }
        roomBlocks = selectedRoomId?.let { loadStandaloneMessages(art, it) }.orEmpty()
    }

    LaunchedEffect(art, currentSenderId, rooms) {
        val params = chatQueryParams()
        if (smartLayout) return@LaunchedEffect
        if (params["screen"] != "ChatModuleScreen" || params["action"] != "start-chat") return@LaunchedEffect
        val targetFirmenId = params["firmenId"].orEmpty().trim()
        val targetUserId = params["userId"].orEmpty().trim()
        if (targetFirmenId.isBlank() || targetUserId.isBlank()) return@LaunchedEffect
        val targetMember = globalChatUserId(targetFirmenId, targetUserId)
        if (targetMember.isBlank() || targetMember == currentSenderId) return@LaunchedEffect
        val targetSet = setOf(currentSenderId, targetMember)
        val existing = rooms.firstOrNull { room -> roomParticipantIds(room).toSet() == targetSet }
        if (existing != null) {
            selectedRoomId = existing.id
            return@LaunchedEffect
        }
        ensureStandaloneRoom(art, targetSet).getOrNull()?.let { room ->
            upsertRoomState(room)
            roomBlocks = loadStandaloneMessages(art, room.id)
        }
    }

    LaunchedEffect(art, currentSenderId, presetTargetChatId, rooms) {
        if (!conversationOnly) return@LaunchedEffect
        if (presetTargetChatId.isBlank() || presetTargetChatId == currentSenderId) return@LaunchedEffect
        val targetSet = setOf(currentSenderId, presetTargetChatId)
        val existing = rooms.firstOrNull { room -> roomParticipantIds(room).toSet() == targetSet }
        if (existing != null) {
            selectedRoomId = existing.id
            return@LaunchedEffect
        }
        ensureStandaloneRoom(art, targetSet).getOrNull()?.let { room ->
            upsertRoomState(room)
            roomBlocks = loadStandaloneMessages(art, room.id)
        }
    }

    LaunchedEffect(selectedRoom?.id, roomBlocks.size) {
        window.setTimeout({
            val container = document.getElementById(CHAT_MESSAGES_CONTAINER_ID) as? HTMLElement
            if (container != null) {
                container.scrollTop = container.scrollHeight.toDouble()
            }
        }, 0)
    }

    fun sendCurrentMessage() {
        if (!canWrite) return
        val room = selectedRoom ?: return
        val text = inputText.trim()
        if (text.isBlank() || isSaving) return

        inputText = ""
        isSaving = true

        scope.launch {
            try {
                val result = persistMessage(art, room, text)
                result.getOrNull()?.let { updated ->
                    upsertRoomState(updated)
                    roomBlocks = loadStandaloneMessages(art, updated.id)
                }
            } finally {
                isSaving = false
            }
        }
    }

    fun startOrOpenChat(memberIds: Set<String>) {
        if (!canWrite) return

        val cleanMembers = memberIds.filter { it.isNotBlank() && it !in currentUserIds }.toSet()
        if (cleanMembers.isEmpty()) return

        val targetSet = (cleanMembers + currentSenderId).toSet()
        val existing = rooms.firstOrNull { room -> roomParticipantIds(room).toSet() == targetSet }
        if (existing != null) {
            selectedRoomId = existing.id
            showContactsView = false
            showNewChat = false
            selectedUsers = emptySet()
            return
        }

        scope.launch {
            ensureStandaloneRoom(art, targetSet).getOrNull()?.let { room ->
                upsertRoomState(room)
                roomBlocks = loadStandaloneMessages(art, room.id)
            }
            showContactsView = false
            showNewChat = false
            selectedUsers = emptySet()
        }
    }

    Div({
        style {
            width(if (popupMode) popupWidth.px else 100.percent)
            height(if (popupMode) popupHeight.px else if (embeddedInEngine) 100.percent else 100.vh)
            backgroundColor(DashboardTheme.SURFACE_DEEP)
            property("background", if (popupMode || embeddedInEngine) DashboardTheme.POPUP_BG else DashboardTheme.PREMIUM_SURFACE_BG)
            padding(if (smartLayout) 12.px else 18.px, if (smartLayout) 14.px else 20.px)
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(if (smartLayout) 10.px else 14.px)
            overflow("hidden")
            borderRadius(if (popupMode) 16.px else 0.px)
            if (!embeddedInEngine) property("border", "1px solid rgba(255,255,255,0.14)")
            minHeight(0.px)
            if (popupMode) {
                position(Position.Fixed)
                left(posX.px)
                top(posY.px)
                property("z-index", "9900")
                property(
                    "box-shadow",
                    "0 24px 60px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.16)"
                )
                property("outline", "1px solid rgba(255,255,255,0.10)")
                property("outline-offset", "-1px")
                property("background-clip", "padding-box")
            }
        }
    }) {
        if (internalTitleVisible) {
            Div({
                style {
                    position(Position.Relative)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(if (smartLayout) JustifyContent.SpaceBetween else JustifyContent.Center)
                    color(Color.white)
                    fontSize(if (smartLayout) 14.px else 26.px)
                    fontWeight(if (smartLayout) "600" else "700")
                    padding(if (smartLayout) 2.px else 4.px, if (popupMode || embeddedInEngine) 30.px else 44.px, 2.px, 0.px)
                    textAlign(if (smartLayout) "left" else "center")
                    width(100.percent)
                    if (popupMode) cursor("grab")
                }
                if (popupMode) onMouseDown {
                    draggingWindow = true
                    startX = it.clientX.toDouble()
                    startY = it.clientY.toDouble()
                }
            }) {
                if (smartLayout) {
                    Span({
                        style {
                            fontSize(12.px)
                            color(Color("rgba(255,255,255,0.72)"))
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                            property("max-width", "42%")
                        }
                    }) { Text(headerTitle) }
                    Span({
                        style {
                            position(Position.Absolute)
                            left(0.px)
                            right(0.px)
                            textAlign("center")
                            fontSize(14.px)
                            fontWeight("600")
                            color(Color.white)
                            property("pointer-events", "none")
                        }
                    }) { Text("Chat") }
                } else {
                    Text(headerTitle)
                }
            }
        }

        if (popupMode && onClose != null) {
            Div({ style { position(Position.Absolute); top(if (smartLayout) 14.px else 22.px); right(if (smartLayout) 14.px else 22.px); property("z-index", "2") } }) {
                Img(src = "images/miniclose.svg", attrs = {
                    onClick { onClose() }
                    style {
                        width(16.px)
                        height(16.px)
                        cursor("pointer")
                        property("filter", "brightness(0) invert(1)")
                    }
                })
            }
        }

        Div({
            style {
                width(100.percent)
                height(1.px)
                backgroundColor(Color("rgba(255,255,255,0.14)"))
                flexShrink(0)
            }
        })

        Div({
            style {
                flexGrow(1)
                minHeight(0.px)
                display(if (smartLayout) DisplayStyle.Flex else DisplayStyle.Grid)
                if (!smartLayout) property("grid-template-columns", "${sidebarWidth}px 8px minmax(0, 1fr)")
                overflow("hidden")
            }
        }) {
            if (!smartLayout) {
                Div({
                    style {
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        overflow("hidden")
                        paddingRight(18.px)
                    }
                }) {
                    Div({
                        classes(ChatPopupStyles.hiddenScroll)
                        style {
                            flexGrow(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                        }
                    }) {
                        if (showContactsView) {
                            if (filteredContactCompanies.isEmpty()) {
                                Div({
                                    style {
                                        color(Color("rgba(255,255,255,0.68)"))
                                        fontSize(12.px)
                                        padding(10.px, 0.px)
                                    }
                                }) { Text("Keine Kontakte vorhanden.") }
                            } else {
                                filteredContactCompanies.forEach { company ->
                                    val companyId = company.firmenId.ifBlank { company.id }
                                    val companyLabel = company.firma.ifBlank { company.kunde.ifBlank { companyId.ifBlank { "Firma" } } }
                                    val expanded = expandedCompanies.contains(companyId)

                                    CompanyHeaderRow(
                                        label = companyLabel,
                                        onClick = {
                                            expandedCompanies =
                                                if (expanded) expandedCompanies - companyId
                                                else expandedCompanies + companyId
                                        }
                                    )

                                    if (expanded) {
                                        company.columnSettings.forEach { user ->
                                            val userId = userChatId(user)
                                            val checked = selectedUsers.contains(userId)

                                            DesktopContactRow(
                                                user = user,
                                                canWrite = canWrite,
                                                showNewChat = showNewChat,
                                                checked = checked,
                                                onChecked = { isChecked ->
                                                    selectedUsers =
                                                        if (isChecked) selectedUsers + userId
                                                        else selectedUsers - userId
                                                },
                                                onClick = {
                                                    if (showNewChat) {
                                                        selectedUsers =
                                                            if (checked) selectedUsers - userId
                                                            else selectedUsers + userId
                                                    } else {
                                                        startOrOpenChat(setOf(userId))
                                                    }
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                        } else if (infoRoomId != null) {
                            val infoRoom = rooms.firstOrNull { it.id == infoRoomId }
                            if (infoRoom == null) {
                                infoRoomId = null
                            } else {
                                val participants = roomParticipantIds(infoRoom)
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(8.px)
                                    }
                                }) {
                                    participants.forEach { participantId ->
                                        val user = allUsers[participantId]
                                        Div({
                                            style {
                                                padding(10.px, 12.px, 10.px, 14.px)
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                gap(8.px)
                                            }
                                        }) {
                                            Img(src = "images/user.svg", attrs = {
                                                style {
                                                    width(24.px)
                                                    height(24.px)
                                                    property("filter", "brightness(0) invert(1)")
                                                    flexShrink(0)
                                                }
                                            })
                                            Div({
                                                style {
                                                    display(DisplayStyle.Flex)
                                                    flexDirection(FlexDirection.Column)
                                                    minWidth(0.px)
                                                }
                                            }) {
                                                Span({
                                                    style {
                                                        fontSize(12.px)
                                                        fontWeight("700")
                                                        color(Color.white)
                                                        property("overflow", "hidden")
                                                        property("text-overflow", "ellipsis")
                                                        property("white-space", "nowrap")
                                                    }
                                                }) {
                                                    Text(
                                                        when {
                                                            participantId in currentUserIds -> "Du"
                                                            user != null -> userDisplayName(user)
                                                            else -> participantId
                                                        }
                                                    )
                                                }
                                                Span({
                                                    style {
                                                        fontSize(11.px)
                                                        color(Color("rgba(255,255,255,0.68)"))
                                                        property("overflow", "hidden")
                                                        property("text-overflow", "ellipsis")
                                                        property("white-space", "nowrap")
                                                    }
                                                }) { Text(user?.let(::userCompanyLabel).orEmpty().ifBlank { "-" }) }
                                            }
                                        }
                                    }
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            gap(8.px)
                                            padding(10.px, 12.px)
                                        }
                                    }) {
                                        LumaticaButton("Zurück", onClick = { infoRoomId = null })
                                        LumaticaButton(
                                            label = "Chat löschen",
                                            onClick = { deleteRoomForCurrentUser(infoRoom) },
                                            variant = LumaticaButtonVariant.Action
                                        )
                                    }
                                }
                            }
                        } else if (filteredRooms.isEmpty()) {
                                Div({
                                    style {
                                        color(Color("rgba(255,255,255,0.68)"))
                                        fontSize(12.px)
                                        padding(10.px, 0.px)
                                    }
                                }) { Text("Noch keine Chats vorhanden.") }
                            } else {
                                filteredRooms.forEachIndexed { index, room ->
                                val active = room.id == selectedRoomId
                                val hovered = room.id == hoveredRoomId
                                val participantIds = roomParticipantIds(room).filter { it !in currentUserIds }
                                val names = participantIds.map { displayNameById(it, allUsers) }
                                val hoverText = participantIds.joinToString("\n") { chatUserHoverLineById(it, allUsers) }
                                val primary = names.firstOrNull().orEmpty().ifBlank { roomTitle(room, currentUserIds, allUsers) }
                                val avatarUser = participantIds.firstOrNull()?.let { allUsers[it] }
                                val moreCount = (names.size - 1).coerceAtLeast(0)

                                Div({
                                    style {
                                        padding(10.px, 12.px, 10.px, 14.px)
                                        cursor("pointer")
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(4.px)
                                        property("border-left", if (active) "4px solid ${DashboardTheme.ACCENT}" else "4px solid transparent")
                                        property("background", if (active) "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.10) 100%)" else "transparent")
                                        property("background-clip", "padding-box")
                                        if (active) property("box-shadow", "inset 0 0 22px rgba(255,255,255,0.12), 0 0 18px rgba(255,255,255,0.10)")
                                        if (index == 0) property("border-top", "1px solid rgba(255,255,255,0.14)")
                                        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
                                        opacity(if (active) 1 else if (hovered) 0.9 else 0.82)
                                    }
                                    onMouseEnter { hoveredRoomId = room.id }
                                    onMouseDown {
                                        longPressTimer?.let { window.clearTimeout(it) }
                                        longPressTimer = window.setTimeout({
                                            openRoomMenu(room)
                                            longPressTimer = null
                                        }, 520)
                                    }
                                    onMouseUp {
                                        longPressTimer?.let { window.clearTimeout(it) }
                                        longPressTimer = null
                                    }
                                    onMouseLeave {
                                        if (hoveredRoomId == room.id) hoveredRoomId = null
                                        longPressTimer?.let { window.clearTimeout(it) }
                                        longPressTimer = null
                                    }
                                    onContextMenu {
                                        it.preventDefault()
                                        openRoomMenu(room)
                                    }
                                    onClick {
                                        if (suppressRoomClick) {
                                            suppressRoomClick = false
                                            return@onClick
                                        }
                                        clearRoomMenu()
                                        selectedRoomId = room.id
                                    }
                                }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Grid)
                                            property("grid-template-columns", "64px minmax(0, 1fr) auto")
                                            alignItems(AlignItems.Center)
                                            gap(10.px)
                                        }
                                        title(hoverText.ifBlank { names.joinToString(", ") })
                                    }) {
                                        UserAvatarCircle(user = avatarUser, fallbackLabel = primary, sizePx = 60, background = UISettings.SALES_KPI_BG)

                                        Div({
                                            style {
                                                display(DisplayStyle.Flex)
                                                flexDirection(FlexDirection.Column)
                                                gap(3.px)
                                                minWidth(0.px)
                                            }
                                        }) {
                                            Span({
                                                style {
                                                    fontSize(16.px)
                                                    fontWeight(if (active) "800" else "700")
                                                    color(DashboardTheme.TEXT_LIGHT)
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                }
                                            }) { Text(if (moreCount > 0) "$primary +$moreCount" else primary) }
                                            Span({
                                                style {
                                                    fontSize(13.px)
                                                    color(DashboardTheme.TEXT_MUTED)
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                }
                                            }) { Text(roomLastMessage(room).ifBlank { "Chat öffnen" }) }
                                        }

                                        roomLastTime(room)?.let { time ->
                                            Span({
                                                style {
                                                    fontSize(11.px)
                                                    color(Color("rgba(255,255,255,0.62)"))
                                                }
                                            }) { Text(time) }
                                        }
                                    }
                                }
                                if (menuRoomId == room.id) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            justifyContent(JustifyContent.FlexEnd)
                                            gap(8.px)
                                            padding(0.px, 12.px, 8.px, 12.px)
                                        }
                                    }) {
                                        LumaticaButton(
                                            label = "Info",
                                            onClick = {
                                                infoRoomId = room.id
                                                clearRoomMenu()
                                            },
                                            heightPx = 32,
                                            paddingH = 12
                                        )
                                        LumaticaButton(
                                            label = "Chat löschen",
                                            onClick = { deleteRoomForCurrentUser(room) },
                                            heightPx = 32,
                                            paddingH = 12
                                        )
                                    }
                                }
                                }
                            }
                    }

                    Div({
                        style {
                            padding(12.px, 0.px, 0.px, 0.px)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            gap(8.px)
                            flexShrink(0)
                        }
                    }) {
                        if (showContactsView) {
                            LumaticaButton(
                                label = "Zurück",
                                onClick = {
                                    showContactsView = false
                                    showNewChat = false
                                    selectedUsers = emptySet()
                                },
                                enabled = canWrite
                            )
                            LumaticaButton(
                                label = "Gruppenchat",
                                onClick = {
                                    showNewChat = !showNewChat
                                    if (!showNewChat) selectedUsers = emptySet()
                                },
                                variant = LumaticaButtonVariant.State,
                                isActive = showNewChat,
                                enabled = canWrite
                            )
                            if (showNewChat) {
                                LumaticaButton(
                                    label = "Chat starten",
                                    onClick = { startOrOpenChat(selectedUsers) },
                                    variant = LumaticaButtonVariant.Action,
                                    enabled = canWrite && selectedUsers.isNotEmpty()
                                )
                            }
                        } else {
                            Img(src = "images/addbox.svg", attrs = {
                                onClick {
                                    if (canWrite) {
                                        showContactsView = true
                                        showNewChat = false
                                        selectedUsers = emptySet()
                                    }
                                }
                                style {
                                    width(38.px)
                                    height(38.px)
                                    cursor(if (canWrite) "pointer" else "default")
                                    opacity(if (canWrite) 1 else 0.45)
                                }
                            })
                        }
                    }
                }

                Div({
                    style {
                        width(8.px)
                        minHeight(0.px)
                        cursor("col-resize")
                        position(Position.Relative)
                    }
                    onMouseDown {
                        resizingSidebar = true
                        startX = it.clientX.toDouble()
                    }
                }) {
                    Div({
                        style {
                            position(Position.Absolute)
                            top(0.px)
                            bottom(0.px)
                            left(3.px)
                            width(1.px)
                            backgroundColor(Color("rgba(255,255,255,0.14)"))
                        }
                    })
                }
            }

            Div({
                style {
                    minHeight(0.px)
                    flexGrow(1)
                    backgroundColor(Color.transparent)
                    property("background", "transparent")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    overflow("hidden")
                    paddingLeft(if (smartLayout) 0.px else 12.px)
                }
            }) {
                Div({
                    attr("id", CHAT_MESSAGES_CONTAINER_ID)
                    classes(ChatPopupStyles.hiddenScroll)
                    style {
                        flexGrow(1)
                        minHeight(0.px)
                        overflowY("auto")
                        padding(6.px, 0.px, 14.px, 0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                    }
                }) {
                    if (selectedRoom == null) {
                        Div({
                            style {
                                flexGrow(1)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                color(Color("rgba(255,255,255,0.7)"))
                                fontSize(14.px)
                                textAlign("center")
                            }
                        }) { Text(if (showContactsView) "Wähle links einen User." else "Wähle links einen Chat.") }
                    } else {
                        ChatMessageList(roomBlocks, { isOwnMessage(it, currentUserIds) }, { chatAuthorLabel(it, currentUserIds, allUsers) })
                    }
                }

                Div({
                    style {
                        minHeight(58.px)
                        padding(8.px, if (smartLayout) 8.px else 0.px, 0.px, if (smartLayout) 10.px else 0.px)
                        property("border-top", "1px solid rgba(255,255,255,0.12)")
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Div({
                        style {
                            width(if (smartLayout) 36.px else 28.px)
                            height(if (smartLayout) 36.px else 28.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            cursor("pointer")
                            opacity(0.88)
                            flexShrink(0)
                        }
                        onClick { smartLayout = !smartLayout }
                    }) {
                        Img(src = if (smartLayout) "images/collapsecontent.svg" else "images/expandcontent.svg", attrs = {
                            style {
                                width(if (smartLayout) 22.px else 18.px)
                                height(if (smartLayout) 22.px else 18.px)
                                property("filter", "brightness(0) invert(1)")
                            }
                        })
                    }

                    Input(InputType.Text, attrs = {
                        value(inputText)
                        style {
                            flexGrow(1)
                            height(if (smartLayout) 36.px else 42.px)
                            padding(0.px, if (smartLayout) 14.px else 12.px)
                            boxSizing("border-box")
                            property("border", "1px solid rgba(255,255,255,0.14)")
                            borderRadius(10.px)
                            backgroundColor(Color.transparent)
                            property("background", "transparent")
                            color(Color.white)
                            fontSize(13.px)
                        }
                        onInput { inputText = it.value }
                        onKeyDown {
                            if ((it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") &&
                                selectedRoom != null &&
                                inputText.isNotEmpty() &&
                                !isSaving
                            ) {
                                it.preventDefault()
                                sendCurrentMessage()
                            }
                        }
                    })

                    Div({
                        style {
                            width(if (smartLayout) 40.px else 28.px)
                            height(if (smartLayout) 36.px else 28.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            cursor(if (selectedRoom != null && inputText.isNotEmpty() && !isSaving) "pointer" else "default")
                            opacity(if (selectedRoom != null && inputText.isNotEmpty() && !isSaving) 0.92 else 0.38)
                            flexShrink(0)
                        }
                        if (selectedRoom != null && inputText.isNotEmpty() && !isSaving) {
                            onClick { sendCurrentMessage() }
                        }
                    }) {
                        Img(src = "images/send.svg", attrs = {
                            style {
                                width(if (smartLayout) 24.px else 28.px)
                                height(if (smartLayout) 24.px else 28.px)
                                property("filter", "brightness(0) invert(1)")
                            }
                        })
                    }
                }
            }
        }

        if (popupMode && smartLayout) {
            Div({
                style {
                    position(Position.Absolute)
                    right(0.px)
                    bottom(0.px)
                    width(18.px)
                    height(18.px)
                    cursor("nwse-resize")
                    property("background", "transparent")
                    property("z-index", "3")
                    boxSizing("border-box")
                }
                onMouseDown {
                    it.stopPropagation()
                    resizingWindow = true
                    startX = it.clientX.toDouble()
                    startY = it.clientY.toDouble()
                    startWidth = popupWidth
                    startHeight = popupHeight
                }
            })
        }
    }

}

@Composable
private fun SystemMessage(text: String) {
    Div({
        style {
            alignSelf(AlignSelf.Center)
            padding(6.px, 10.px)
            borderRadius(999.px)
            color(Color("rgba(255,255,255,0.78)"))
            fontSize(11.px)
            property("max-width", "80%")
            textAlign("center")
        }
    }) { Text(text) }
}

@Composable
fun ChatBubble(block: All, own: Boolean, authorLabel: String, timeLabel: String = "") {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(if (own) JustifyContent.FlexEnd else JustifyContent.FlexStart)
            marginBottom(2.px)
        }
    }) {
        Div({
            style {
                property("max-width", "76%")
                color(Color.white)
                borderRadius(12.px)
                padding(10.px, 12.px)
                property("white-space", "pre-wrap")
                property("tab-size", "1")
                property("word-break", "break-word")
                backgroundColor(if (own) Color("rgba(255,255,255,0.12)") else Color("rgba(255,255,255,0.05)"))
                property("border", "1px solid rgba(255,255,255,0.14)")
            }
        }) {
            if (authorLabel.isNotBlank()) {
                Div({
                    style {
                        marginBottom(6.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(11.px)
                            fontWeight("700")
                            lineHeight("1.2")
                            color(Color("rgba(255,255,255,0.9)"))
                        }
                    }) { Text(authorLabel) }
                }
            }
            Span({ style { fontSize(13.px); lineHeight("1.45") } }) {
                Text(block.bemerkung)
            }
            if (block.linkedinNachrichtAnhaenge.isNotBlank()) Div({ style { fontSize(12.px); padding(6.px, 0.px) } }) { Text(block.linkedinNachrichtAnhaenge) }
            if (timeLabel.isNotBlank()) Span({ style { property("float", "right"); property("margin-left", "12px"); property("margin-top", "6px"); fontSize(10.px); color(Color("rgba(255,255,255,.65)")) } }) { Text(timeLabel) }
        }
    }
}











